import { useState, useEffect, useRef, Fragment, Component } from 'react';
import axios from 'axios';
import '../styles/proposalPrint.css';
import { useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useReactToPrint } from 'react-to-print';
import html2pdf from 'html2pdf.js';

import { CssBaseline, Box, Typography, Paper, Button } from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';

import {
  Spinner,
  BrowserTitle,
  ProposalViewHeader,
  ProposalViewEditSection,
  ProposalViewAwards,
  ProposalViewContacts,
  ProposalViewAdditionalInfo,
  ProposalViewJobOverview,
  ProposalViewEquipmentOverview,
  ProposalViewAirflow,
  ProposalViewWorkDescription,
  ProposalViewInvestment,
  ProposalViewGuarantees,
  ProposalViewFooter,
  ProposalViewSignature,
  EquipmentImage,
} from '../components';

import messages from '../static/messages';
import { calculateTotalPay, calculateTotalRebates, calculateTotalInstants } from '../utils/proposalCalculations';
import { API_URL } from '../api/axios';

class SmsViewErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  componentDidCatch(error, info) {
    console.error('[ProposalSmsView] Render error:', error, info);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: 40, textAlign: 'center' }}>
          <h2 style={{ color: '#d32f2f' }}>Something went wrong</h2>
          <pre style={{ textAlign: 'left', maxWidth: 600, margin: '20px auto', fontSize: 12, background: '#f5f5f5', padding: 16, borderRadius: 8, overflow: 'auto' }}>
            {this.state.error?.toString()}
          </pre>
        </div>
      );
    }
    return this.props.children;
  }
}

const SmsReceiptView = ({ proposal }) => {
  // Submitted (unconfirmed) payment info must never appear on client documents.
  const payments = (proposal.payments || []).filter((p) => p.status !== 'submitted');
  const paymentsTotal = payments.reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);
  const serviceTotal = proposal.proposalType === 'Service'
    ? (proposal.workDescriptions || []).reduce((sum, w) => sum + (parseFloat(w.price) || 0), 0)
    : 0;
  const totalPaid = paymentsTotal > 0
    ? paymentsTotal
    : (serviceTotal || parseFloat(proposal.jobPrice) || parseFloat(proposal.data?.totalPay) || 0);
  const paymentMethodFromPayments = payments.map(p => p.methodName).filter(Boolean).join(', ');
  const paymentMethod = paymentMethodFromPayments
    || proposal.paymentNotes
    || proposal.additionalNotes?.payment?.comment
    || null;
  const receiptId = proposal.receiptId || `RCPT-${proposal.publicId}`;
  const client = proposal.property?.clients?.[0]?.client;
  const clientName = client?.fullName || 'Customer';

  return (
    <SmsViewErrorBoundary>
      <style>{`
        @media print {
          .no-print { display: none !important; }
          body { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; background: white !important; }
          .receipt-container { box-shadow: none !important; margin: 0 !important; }
          @page { margin: 0.5in; size: letter; }
        }
        @media screen { body { background-color: #f5f5f5; } }
      `}</style>

      <Box className="no-print" sx={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 1000,
        backgroundColor: '#0d3c61', padding: 2, display: 'flex', justifyContent: 'center', gap: 2,
        boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
      }}>
        <Button variant="contained" startIcon={<PrintIcon />} onClick={() => window.print()}
          sx={{ backgroundColor: '#fff', color: '#0d3c61', '&:hover': { backgroundColor: '#e3f2fd' } }}>
          Print
        </Button>
      </Box>

      <Box className="no-print" sx={{ height: 80 }} />

      <Box className="receipt-container" sx={{
        maxWidth: 650, margin: '20px auto', backgroundColor: '#fff',
        borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', overflow: 'hidden'
      }}>
        <Box sx={{ borderBottom: '2px solid #0d3c61', padding: 4, textAlign: 'center' }}>
          <img src="/images/Airmaxx-Logo-Trans.png" alt="Airmaxx" style={{ maxHeight: 60, marginBottom: 8 }} />
          <Typography sx={{ color: '#333', fontSize: 14 }}>1865 John Towers Ave, Ste B, El Cajon, CA 92020</Typography>
        </Box>

        <Box sx={{ textAlign: 'center', py: 3, backgroundColor: '#fafafa' }}>
          <Box sx={{
            display: 'inline-block', backgroundColor: '#4caf50', color: '#fff',
            px: 4, py: 1.5, borderRadius: 1, fontWeight: 'bold', fontSize: 16
          }}>
            ☑ PAID IN FULL
          </Box>
        </Box>

        <Box sx={{ display: 'flex', gap: 3, p: 4, flexWrap: 'wrap' }}>
          <Box sx={{ flex: 1, minWidth: 200 }}>
            <Typography variant="subtitle2" sx={{ color: '#0d3c61', fontWeight: 'bold', textTransform: 'uppercase', borderBottom: '2px solid #0d3c61', pb: 1, mb: 2, fontSize: 14 }}>
              Receipt Information
            </Typography>
            <Typography sx={{ fontSize: 13, color: '#666', mb: 1 }}><strong>Receipt #:</strong> {receiptId}</Typography>
            <Typography sx={{ fontSize: 13, color: '#666', mb: 1 }}>
              <strong>Date Issued:</strong> {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
            </Typography>
            {payments.length > 0 && payments[0].date && (
              <Typography sx={{ fontSize: 13, color: '#666', mb: 1 }}>
                <strong>Payment Date:</strong> {new Date(payments[0].date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
              </Typography>
            )}
            {paymentMethod && <Typography sx={{ fontSize: 13, color: '#666' }}><strong>Payment Method:</strong> {paymentMethod}</Typography>}
          </Box>

          <Box sx={{ flex: 1, minWidth: 200 }}>
            <Typography variant="subtitle2" sx={{ color: '#0d3c61', fontWeight: 'bold', textTransform: 'uppercase', borderBottom: '2px solid #0d3c61', pb: 1, mb: 2, fontSize: 14 }}>
              Customer Information
            </Typography>
            <Typography sx={{ fontSize: 13, color: '#666', mb: 1, fontWeight: 'bold' }}>{clientName}</Typography>
            {proposal.property?.address && <Typography sx={{ fontSize: 13, color: '#666', mb: 1 }}>{proposal.property.address}</Typography>}
            {client?.phone && <Typography sx={{ fontSize: 13, color: '#666' }}>{client.phone}</Typography>}
            {client?.email && <Typography sx={{ fontSize: 13, color: '#666' }}>{client.email}</Typography>}
          </Box>
        </Box>

        <Box sx={{ px: 4, pb: 4 }}>
          <Typography variant="subtitle2" sx={{ color: '#0d3c61', fontWeight: 'bold', textTransform: 'uppercase', borderBottom: '2px solid #0d3c61', pb: 1, mb: 2, fontSize: 14 }}>
            Services Provided
          </Typography>
          <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', backgroundColor: '#f5f5f5', borderBottom: '1px solid #e0e0e0', p: 1.5 }}>
              <Typography sx={{ fontSize: 12, fontWeight: 'bold', color: '#333' }}>
                {proposal.proposalType === 'Service' ? 'Service Description' : 'Project Number'}
              </Typography>
            </Box>
            {proposal.proposalType === 'Service' && proposal.workDescriptions?.length > 0 ? (
              proposal.workDescriptions.map((work, index) => (
                <Box key={index} sx={{ display: 'flex', justifyContent: 'space-between', p: 2, borderBottom: '1px solid #e0e0e0' }}>
                  <Typography sx={{ fontSize: 14, color: '#333', flex: 1, pr: 2 }}>{work.description}</Typography>
                </Box>
              ))
            ) : (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', p: 2, borderBottom: '1px solid #e0e0e0' }}>
                <Typography sx={{ fontSize: 14, color: '#333' }}>{proposal.publicId}</Typography>
              </Box>
            )}
            {payments.length > 0 && payments.map((payment, index) => (
              <Box key={index} sx={{
                display: 'flex', justifyContent: 'space-between', p: 1.5, backgroundColor: '#fafafa',
                borderBottom: index < payments.length - 1 ? '1px solid #e0e0e0' : 'none'
              }}>
                <Typography sx={{ fontSize: 12, color: '#666' }}>
                  {payment.methodName || 'Payment'} {payment.reference ? `(Ref: ${payment.reference})` : ''}
                  {payment.date ? ` - ${new Date(payment.date).toLocaleDateString()}` : ''}
                </Typography>
                <Typography sx={{ fontSize: 12, color: '#666' }}>
                  ${parseFloat(payment.amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                </Typography>
              </Box>
            ))}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', backgroundColor: '#e8f5e9', p: 2 }}>
              <Typography sx={{ fontSize: 14, fontWeight: 'bold', color: '#2e7d32' }}>Total Paid:</Typography>
              <Typography sx={{ fontSize: 18, fontWeight: 'bold', color: '#2e7d32' }}>
                ${totalPaid.toLocaleString('en-US', { minimumFractionDigits: 2 })}
              </Typography>
            </Box>
          </Paper>
        </Box>

        <Box sx={{ textAlign: 'center', px: 4, pb: 4 }}>
          <Typography variant="h5" sx={{ color: '#0d3c61', fontWeight: 'bold', mb: 2 }}>Thank you for your business!</Typography>
          <Typography sx={{ fontSize: 13, color: '#666', lineHeight: 1.6 }}>
            This receipt confirms payment in full for all services rendered.<br />Please keep for your records.
          </Typography>
        </Box>

        <Box sx={{ backgroundColor: '#f8f9fa', p: 3, textAlign: 'center' }}>
          <Typography sx={{ fontSize: 12, color: '#666', mb: 1 }}>For questions or concerns, contact us at</Typography>
          <Typography sx={{ fontSize: 12, color: '#0d3c61', fontWeight: 'bold' }}>619-655-3010 | support@airmaxx.com</Typography>
        </Box>
      </Box>

      <Box className="no-print" sx={{ height: 40 }} />
    </SmsViewErrorBoundary>
  );
};

const fontFamily = '"Inter", sans-serif';

const ProposalSmsView = () => {
  const [proposal, setProposal] = useState();
  const [pdfLoading, setPdfLoading] = useState(false);
  const [equipmentTypes, setEquipmentTypes] = useState({});
  const [smallLogo, setSmallLogo] = useState(false);
  const [notFound, setNotFound] = useState(false);

  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const viewMode = searchParams.get('v');
  const printRef = useRef();

  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    documentTitle: `proposal_${proposal?.publicId || 'document'}`,
    onAfterPrint: () => toast.info('Document printed'),
  });

  const handleDownloadPdf = () => {
    if (!printRef.current) return;
    setPdfLoading(true);

    const element = printRef.current;
    const noPrintEls = element.querySelectorAll('.no-print');
    noPrintEls.forEach(el => el.style.display = 'none');

    const opt = {
      margin: [10, 10, 10, 10],
      filename: `Proposal-${proposal?.publicId || 'document'}.pdf`,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true, letterRendering: true },
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
      pagebreak: { mode: ['css', 'legacy'] }
    };

    html2pdf()
      .set(opt)
      .from(element)
      .save()
      .then(() => {
        setPdfLoading(false);
        noPrintEls.forEach(el => el.style.display = '');
        toast.info('PDF downloaded');
      })
      .catch((err) => {
        setPdfLoading(false);
        noPrintEls.forEach(el => el.style.display = '');
        console.error(err);
        toast.error('Failed to generate PDF');
      });
  };

  const getProposal = async () => {
    try {
      const response = await axios.get(`${API_URL}/public/proposal/sms/${token}`);
      if (response.status === 200) {
        setProposal(response.data);
      }
    } catch (error) {
      console.error(error);
      if (error.response?.status === 404) {
        setNotFound(true);
      } else {
        toast.error(messages.errors.error_data_loaning);
      }
    }
  };

  const handleSignProposal = async (signData) => {
    try {
      const response = await axios.patch(`${API_URL}/public/proposal/sms/${token}`, {
        signature: { sign: signData }
      });
      if (response.status === 200) {
        setProposal(response.data);
        toast.success('Proposal signed successfully! Thank you.');
      }
    } catch (error) {
      console.error('Signing error:', error);
      toast.error('Failed to sign proposal. Please try again.');
    }
  };

  const getEquipmentTypes = async () => {
    try {
      const response = await axios.get(`${API_URL}/equipment-types`);
      if (response?.data?.data) {
        const typesMap = {};
        response.data.data.forEach(type => { typesMap[type.id] = type; });
        setEquipmentTypes(typesMap);
      }
    } catch (error) {
      console.log('Equipment types load error:', error);
    }
  };

  useEffect(() => {
    if (token) {
      getProposal();
      getEquipmentTypes();
    }
  }, [token]);

  if (notFound) {
    return (
      <Box sx={{
        display: 'flex', flexDirection: 'column',
        justifyContent: 'center', alignItems: 'center',
        minHeight: '100vh', backgroundColor: '#f5f5f5'
      }}>
        <Typography variant="h4" sx={{ color: '#666', mb: 2 }}>
          Proposal Not Found
        </Typography>
        <Typography variant="body1" sx={{ color: '#999' }}>
          This link may have expired or the proposal no longer exists.
        </Typography>
      </Box>
    );
  }

  if (!proposal) return <Spinner />;

  const systems = proposal?.proposalSystems || [];
  const isServiceProposal = proposal?.proposalType === 'Service';

  if (viewMode === 'receipt') {
    return <SmsReceiptView proposal={proposal} />;
  }

  const totalPay = calculateTotalPay(proposal);
  const totalRebates = calculateTotalRebates(proposal);
  const totalInstants = calculateTotalInstants(proposal);

  const getJobOverview = () => {
    if (proposal.jobOverview) return proposal.jobOverview;
    if (proposal.proposalSystems) {
      const parts = [];
      proposal.proposalSystems.forEach(s => { if (s.notes) parts.push(s.notes); });
      if (parts.length > 0) return parts.join('\n\n');
    }
    return null;
  };

  const handleScrollToSignature = () => {
    const el = document.getElementById('signature-section');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  const sectionHeader = (title) => (
    <Box sx={{ display: 'inline-block', mb: 0 }}>
      <Box sx={{ backgroundColor: '#0d3c61', color: '#fff', px: 2, py: 0.75 }}>
        <Typography sx={{ fontFamily, fontWeight: 600, fontSize: 14, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
          {title}
        </Typography>
      </Box>
    </Box>
  );

  return (
    <SmsViewErrorBoundary>
    <Fragment>
      <CssBaseline />
      <BrowserTitle title={`Proposal ${proposal?.publicId || ''} | Airmaxx Pro`} />

      <ProposalViewHeader
        proposal={proposal}
        handlePdf={handleDownloadPdf}
        handlePrint={handlePrint}
        pdfLoading={pdfLoading}
        token={true}
        totalPay={totalPay}
        isAccepted={!!proposal.signature}
        handleSignatureModalOpen={handleScrollToSignature}
      />

      <Box
        component="main"
        className="proposal-print-container"
        sx={{
          background: 'linear-gradient(135deg, #f5f7fa 0%, #e4e8eb 100%)',
          pt: { xs: '70px', sm: '80px' },
          pb: { xs: 2, sm: 5 },
          fontFamily,
          minHeight: '100vh',
          '@media print': {
            background: 'white !important',
            padding: '0 !important',
            minHeight: 'auto !important',
          },
        }}
      >
        <ProposalViewEditSection id={proposal.id} client={true} smallLogo={smallLogo} setSmallLogo={setSmallLogo} />

        <Paper
          ref={printRef}
          sx={{
            maxWidth: 850,
            margin: '1rem auto 0',
            padding: '0',
            fontFamily,
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            borderRadius: 2,
            overflow: 'hidden',
            '@media print': {
              maxWidth: '100% !important',
              width: '100% !important',
              margin: '0 !important',
              boxShadow: 'none !important',
              borderRadius: '0 !important',
            },
          }}
        >
          <Box sx={{ p: 3 }}>
            <ProposalViewAwards
              techName={proposal.createdBy?.fullName}
              techPhone={proposal.createdBy?.phoneNumber}
              techEmail={proposal.createdBy?.email}
              proposalId={proposal.publicId}
              issueDate={proposal.createdAt}
              installationSchedule={proposal.installationSchedule || []}
              smallLogo={smallLogo}
            />

            <ProposalViewContacts property={proposal.property} selectedClientId={proposal.selectedClientId} billingAddress={proposal.billingAddress} />

            {!isServiceProposal && <ProposalViewJobOverview description={getJobOverview()} />}
            {isServiceProposal && <ProposalViewEquipmentOverview description={proposal.equipmentOverview} />}

            {/* Document Type Title */}
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
              <Typography
                sx={{
                  fontFamily, fontWeight: 700, fontSize: 20, color: '#0d3c61',
                  pb: 0.75, textTransform: 'uppercase', letterSpacing: '0.5px',
                  borderBottom: '2px solid #0d3c61',
                }}
              >
                {isServiceProposal ? 'Proposal' : 'Home Improvement Contract'}
              </Typography>
            </Box>

            {/* SERVICE: Work Description */}
            {isServiceProposal && proposal.workDescriptions?.length > 0 && (
              <ProposalViewWorkDescription items={proposal.workDescriptions} />
            )}

            {/* INSTALLATION: Equipment */}
            {!isServiceProposal && systems.length > 0 && (() => {
              const systemsWithEquipment = systems
                .filter(s => s.equipments && s.equipments.length > 0)
                .map(s => ({ ...s, equipments: [...s.equipments].sort((a, b) => (a.orderIndex || 0) - (b.orderIndex || 0)) }));
              if (systemsWithEquipment.length === 0) return null;
              const showSystemNames = systems.length > 1;
              return (
                <Box className="print-section pdf-section" sx={{ mb: 4, px: 2 }}>
                  {sectionHeader('Equipment')}
                  {systemsWithEquipment.map((system, sysIdx) => (
                    <Box key={system.id || sysIdx} sx={{ mb: sysIdx < systemsWithEquipment.length - 1 ? 3 : 0 }}>
                      {showSystemNames && (
                        <Box sx={{ mb: 1.5 }}>
                          <Typography sx={{ fontFamily, fontSize: 13, fontWeight: 600, color: '#0d3c61', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '2px solid #e0e0e0', pb: 0.5, display: 'inline-block' }}>
                            {system.name}
                          </Typography>
                        </Box>
                      )}
                      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)' }, gap: { xs: 1, sm: 2 } }}>
                        {system.equipments.map((eq, idx) => {
                          const imgUrl = eq.imageUrl || equipmentTypes[eq.equipmentType] || null;
                          return (
                            <Box key={eq.id || idx} sx={{ backgroundColor: '#f8f9fa', borderRadius: 1, p: { xs: 1.5, sm: 2 }, display: 'flex', flexDirection: 'column' }}>
                              {imgUrl && (
                                <Box sx={{ mb: 2, display: 'flex', justifyContent: 'center' }}>
                                  <EquipmentImage src={imgUrl} alt={eq.equipmentName || eq.modelNumber} />
                                </Box>
                              )}
                              <Typography sx={{ fontFamily, fontSize: 14, fontWeight: 500, color: '#1a1a1a', mb: 0.5 }}>
                                {eq.equipmentName || eq.modelNumber}
                              </Typography>
                              {eq.description && (
                                <Typography sx={{ fontFamily, fontSize: 12, color: '#666', lineHeight: 1.5 }}>{eq.description}</Typography>
                              )}
                            </Box>
                          );
                        })}
                      </Box>
                    </Box>
                  ))}
                </Box>
              );
            })()}

            {/* Airflow */}
            {!isServiceProposal && proposal.proposalAirflows?.length > 0 && (
              <ProposalViewAirflow airflows={proposal.proposalAirflows} />
            )}

            {/* INSTALLATION: Scope of Work */}
            {!isServiceProposal && (() => {
              let allScopeOfWorks = [];
              if (proposal.scopeOfWorks?.length > 0) {
                allScopeOfWorks = proposal.scopeOfWorks.map(w => ({ ...w }));
              } else if (proposal.proposalSystems) {
                proposal.proposalSystems.forEach(s => {
                  if (s.scopeOfWorks?.length > 0) s.scopeOfWorks.forEach(w => allScopeOfWorks.push({ ...w }));
                });
              }
              if (allScopeOfWorks.length === 0) return null;
              return (
                <Box className="print-section pdf-section" sx={{ mb: 4, px: 2 }}>
                  <Typography sx={{ fontFamily, fontWeight: 700, fontSize: 20, color: '#0d3c61', textAlign: 'center', mb: 3 }}>
                    Scope of Work
                  </Typography>
                  {sectionHeader('Description')}
                  <Box sx={{ backgroundColor: '#f8f9fa', borderRadius: 1, p: 2 }}>
                    {allScopeOfWorks.map((work, idx) => (
                      <Box key={idx} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, py: 1 }}>
                        <Box sx={{ width: 18, height: 18, borderRadius: '50%', backgroundColor: '#4caf50', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, mt: 0.2 }}>
                          <Typography sx={{ color: '#fff', fontSize: 12, fontWeight: 600 }}>✓</Typography>
                        </Box>
                        <Typography sx={{ fontFamily, fontSize: 13, color: '#333', lineHeight: 1.6 }}>{work.description}</Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              );
            })()}

            {/* INSTALLATION: Warranties */}
            {!isServiceProposal && (() => {
              let allWarranties = [];
              if (proposal.warranties?.length > 0) {
                allWarranties = proposal.warranties.map(w => ({ ...w }));
              } else if (proposal.proposalSystems) {
                proposal.proposalSystems.forEach(s => {
                  if (s.warranties?.length > 0) s.warranties.forEach(w => allWarranties.push({ ...w }));
                });
              }
              if (allWarranties.length === 0) return null;
              return (
                <Box className="print-section pdf-section" sx={{ mb: 4, px: 2 }}>
                  {sectionHeader('Warranties')}
                  <Box sx={{ backgroundColor: '#f8f9fa', borderRadius: 1, overflow: 'hidden' }}>
                    {allWarranties.map((warranty, idx) => (
                      <Box key={idx} sx={{ display: 'flex', justifyContent: 'space-between', p: 1.5, borderBottom: idx < allWarranties.length - 1 ? '1px solid #e0e0e0' : 'none' }}>
                        <Typography sx={{ fontFamily, fontSize: 13, color: '#333' }}>{warranty.description}</Typography>
                        <Typography sx={{ fontFamily, fontSize: 13, fontWeight: 600, color: '#0d3c61' }}>
                          {isNaN(warranty.years) ? warranty.years : `${warranty.years} Years`}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              );
            })()}

            {/* Investment */}
            <Box className="print-section pdf-section">
              <ProposalViewInvestment
                totalPay={totalPay}
                rebates={totalRebates}
                instants={totalInstants}
                discountsList={proposal?.discounts || []}
                rebatesList={proposal?.contractRebates || proposal?.rebates || []}
                financingOptions={proposal?.paymentNotes || proposal?.financingOptions}
                isSigned={!!(proposal?.signatureId || proposal?.signature)}
              />
            </Box>

            {/* Guarantees */}
            <Box className="print-section pdf-section">
              <ProposalViewGuarantees guarantees={proposal?.guarantees} />
            </Box>

            {/* Signature */}
            <Box id="signature-section" className="print-section pdf-section">
              <ProposalViewSignature
                signature={proposal.signature}
                onSign={handleSignProposal}
                token={true}
                isAccepted={!!proposal.signature}
                customerName={((proposal.selectedClientId && proposal.property?.clients?.find((c) => c.client?.id === proposal.selectedClientId)?.client) || proposal.property?.clients?.[0]?.client)?.fullName}
              />
            </Box>

            {/* Terms and Conditions link */}
            <Box sx={{ textAlign: 'center', mb: 3, '@media print': { display: 'none' } }}>
              <Typography
                component="a"
                href="https://airmaxx.com/wp-content/uploads/2022/06/Home-Improvement-Construction-Contract-_-Owner-and-Contractor.pdf"
                target="_blank"
                rel="noReferrer"
                sx={{ fontFamily, fontSize: 12, color: '#0d3c61', textDecoration: 'underline', '&:hover': { color: '#0a2d4a' } }}
              >
                View Full Terms and Conditions
              </Typography>
            </Box>

            {/* Additional Info */}
            {proposal.informationForCustomer?.length > 0 && (
              <Box className="additional-info-section no-print">
                <ProposalViewAdditionalInfo info={proposal.informationForCustomer} />
              </Box>
            )}

            {/* Footer */}
            <ProposalViewFooter
              techPhone={proposal.createdBy?.phoneNumber}
              techEmail={proposal.createdBy?.email}
            />
          </Box>
        </Paper>
      </Box>
    </Fragment>
    </SmsViewErrorBoundary>
  );
};

export default ProposalSmsView;
